import { Experiment, Feature } from "./experiment";
import { isWindowUndefined } from "./utils";
import { ensureUserTestId } from "@vp/ab-test-cookie";
import { PageParameters } from "./pageParameter";

const EXPHUB_COOKIE_NAME = "expCtx";
const FEATURE_COOKIE_NAME = "expFlagCtx";
const LS_IMPRESSION_KEY = "exp-ab-impression";
const LS_TTL = 60000;

interface TrackProperties extends PageParameters {
  experimentName: string;
  variationName: string;
  nonInteraction: number;
  impressionEventVersion: number;
}

interface TrackFeatureProperties extends PageParameters {
  flagName: string;
  passes: boolean;
  ruleId: string;
}

function parse(cookieString: string) {
  var result: { [k: string]: string } = {};
  var cookies = cookieString ? cookieString.split("; ") : [];
  for (const cookie of cookies) {
    var parts = cookie.split("=");
    result[parts[0]] = decodeURIComponent(parts.slice(1).join("="));
  }
  return result;
}

const bypassExperimentation = () =>
  window.location.search?.includes("exp_param_ignore");

const get = (name: string) => {
  let cookie = parse(document.cookie)[name];
  if (!cookie) {
    cookie = localStorage.getItem(name) || "";
  }
  return cookie;
};

const logErrorMsg = (name: string, error: any) => {
  console.warn(`AB reader failed in ${name}`, error);
};

/** @internal */
export const getAllExperiments = (): Experiment[] => {
  try {
    if (bypassExperimentation()) {
      console.log(
        "(exp_param_ignore) requests to bypass experimentation, will return empty list"
      );
      return [];
    }
    const expHubCookie = get(EXPHUB_COOKIE_NAME);
    if (!expHubCookie && !window.experimentClickedAlreadyFired) {
      fireExperimentClicked();
    }
    if (expHubCookie) {
      return deserialize(expHubCookie, "|");
    }
  } catch (e) {
    logErrorMsg("getAllExperiments", e);
  }
  return [];
};

/** @internal */
export const getVariation = (experimentKey: string): string | null => {
  try {
    const experiments = getAllExperiments();
    const experiment = experiments.find(
      (e: Experiment) => e.experimentKey === experimentKey
    );
    return experiment ? experiment.variationKey : null;
  } catch (e) {
    logErrorMsg("getVariation", e);
    return null;
  }
};

/** @internal */
const getAllFeatures = (): Feature[] => {
  try {
    if (bypassExperimentation()) {
      console.log(
        "(exp_param_ignore) requests to bypass experimentation, will return empty list"
      );
      return [];
    }
    const featuresCookie = get(FEATURE_COOKIE_NAME);
    if (!featuresCookie && !window.experimentClickedAlreadyFired) {
      fireExperimentClicked();
    }
    if (featuresCookie) {
      return deserializeFeatures(featuresCookie, "|");
    }
  } catch (e) {
    logErrorMsg("getAllFeatures", e);
  }
  return [];
};

/** @internal */
export const checkFeature = (featureName: string): Feature | null => {
  try {
    const features = getAllFeatures();
    const feature = features.find(
      (e: Feature) => e.featureName === featureName
    );
    return feature ?? null;
  } catch (e) {
    logErrorMsg("getVariation", e);
    return null;
  }
};

/** @internal */
export const isAvailable = (): boolean => {
  try {
    let cookie = get(EXPHUB_COOKIE_NAME);
    return !!cookie;
  } catch (e) {
    logErrorMsg("isAvailable", e);
    return false;
  }
};

/**
 * Only for backward compatibility
 * @internal
 * */
export const whenAvailable = (
  callback: (available?: boolean) => void
): void => {
  callback(isAvailable());
};

/** @internal */
export const getTestUserId = (): string => {
  return ensureUserTestId();
};

/** @internal */
export function fireImpression(
  experimentKey: string,
  variationKey: string,
  pageParameters?: PageParameters | null
) {
  if (!experimentKey || !variationKey) {
    logErrorMsg(
      "fireImpression",
      "Invalid input parameters for the fireImpression"
    );
    return false;
  }
  let trackProperties: TrackProperties = {
    experimentName: experimentKey,
    variationName: variationKey,
    nonInteraction: 1,
    impressionEventVersion: 2,
  };
  if (pageParameters) {
    if (
      pageParameters.pageName &&
      pageParameters.pageSection &&
      pageParameters.pageStage
    ) {
      trackProperties = {
        ...trackProperties,
        pageName: pageParameters.pageName,
        pageSection: pageParameters.pageSection,
        pageStage: pageParameters.pageStage,
      };
    } else {
      logErrorMsg("fireImpression", "Page parameters format is wrong");
    }
  }
  try {
    if (experimentAlreadyFired(experimentKey, variationKey)) {
      return false;
    }
    trackInternal("Experiment Viewed", trackProperties);
    setExperimentFired(experimentKey, variationKey);
    return true;
  } catch (error) {
    logErrorMsg("fireImpression", error);
    return false;
  }
}

/** @internal */
export function fireFeatureImpression(
  featureName: string,
  ruleName: string,
  enabled: string
) {
  if (!featureName || !ruleName || !enabled) {
    logErrorMsg(
      "fireImpression",
      "Invalid input parameters for the fireFeatureImpression"
    );
    return false;
  }
  try {
    if (experimentAlreadyFired(featureName, enabled)) {
      return false;
    }
    let trackProperties: TrackFeatureProperties = {
      flagName: featureName,
      passes: enabled === "true",
      ruleId: ruleName,
    };
    trackInternal("Experiment Flag Viewed", trackProperties);
    setExperimentFired(featureName, enabled);
    return true;
  } catch (error) {
    logErrorMsg("fireFeatureImpression", error);
    return false;
  }
}

const deserialize = (rawContext: string, splitter: string): Experiment[] => {
  const experiments: Experiment[] = [];

  const versions = rawContext.split("~");

  if (versions !== null && versions.length === 2) {
    const rawExperimentString = versions[1];
    const rawExperiments = rawExperimentString.split(splitter);

    rawExperiments.forEach((experiment: string) => {
      const experimentData = experiment.split(":");
      let isValidKey: boolean = true;
      if (experimentData.length != 2) {
        console.warn("Experiment context is malformed: ", rawContext);
        isValidKey = false;
      }
      const experimentKey = experimentData[0].trim();
      if (
        experiments.some((e: Experiment) => e.experimentKey === experimentKey)
      ) {
        console.warn(
          `Experiment context has more than one instance of experiment key ${experimentKey}`,
          rawContext
        );
        isValidKey = false;
      }
      if (isValidKey) {
        experiments.push({
          experimentKey: experimentKey,
          variationKey: experimentData[1].trim(),
        });
      }
    });
  }

  return experiments;
};

const deserializeFeatures = (
  rawContext: string,
  splitter: string
): Feature[] => {
  const features: Feature[] = [];

  const versions = rawContext.split("~");

  if (versions !== null && versions.length === 2 && versions[1].length > 0) {
    const rawExperimentString = versions[1];
    const rawExperiments = rawExperimentString.split(splitter);

    rawExperiments.forEach((feature: string) => {
      const featureData = feature.split("^");
      let isValidKey: boolean = true;
      if (featureData.length != 3) {
        console.warn("Features context is malformed: ", rawContext);
        isValidKey = false;
      }
      const featureName = featureData[0].trim();
      if (features.some((e: Feature) => e.featureName === featureName)) {
        console.warn(
          `Feature context has more than one instance of feature name ${featureName}`,
          rawContext
        );
        isValidKey = false;
      }
      if (isValidKey) {
        features.push({
          featureName: featureName,
          ruleName: featureData[1].trim(),
          enabled: featureData[2].trim(),
        });
      }
    });
  }

  return features;
};
const requestCookie = async () => {
  try {
    const result = await fetch(
      `https://context-init-prod.cf-exp.vpsvc.com/?requestUrl=${encodeURIComponent(
        window.location.origin
      )}&testUserId=${ensureUserTestId()}`
    );
    const resultObject: { name: string; destination: string; value: string }[] =
      await result.json();
    resultObject.forEach((entry) => {
      if (entry.destination === "client" && entry.name === "Set-Cookie") {
        document.cookie = entry.value;
      }
      if (entry.destination === "client" && entry.name === "Add-Script") {
        const regex =
          /localStorage\.setItem\(\s*"(?<key>\w+)"\s*,\s*"(?<value>[^\s]+)"\s*\)/g;
        let match = null;
        let counter = 5;
        do {
          match = regex.exec(entry.value)?.groups;
          if (match?.key) {
            localStorage.setItem(match.key, match.value);
          }
        } while (match != null && --counter > 0);
      }
    });
    window.dispatchEvent(new Event("variationsResolved"));
  } catch (e) {
    console.warn("Experiment conext initialization failed");
  }
};

const fireExperimentClicked = () => {
  const trackProperties = {
    eventDetail: "context_not_available",
    experimentName: "cookieless_exp_context",
  };
  // @ts-ignore
  if (window?.tracking?.track) {
    sendTrackEvent(trackProperties);
  } else {
    window.addEventListener("trackingReady", function () {
      sendTrackEvent(trackProperties);
    });
  }
  window.experimentClickedAlreadyFired = true;

  function sendTrackEvent(props: any) {
    // @ts-ignore
    // https://app.segment.com/vistaprint/protocols/tracking-plans/rs_1Uvr60Jg3ROZK4boO6jKvuUj2du
    window.tracking.track("Experiment Clicked", props);
  }
};

function getImpressionCache() {
  const newImpressionCache = {
    _ttl: new Date().getTime() + LS_TTL,
    testUserId: ensureUserTestId(),
    experiments: {},
  };
  try {
    const impressionCache = JSON.parse(
      localStorage.getItem(LS_IMPRESSION_KEY) || "{}"
    );
    if (
      impressionCache &&
      impressionCache._ttl > new Date() &&
      impressionCache.testUserId === ensureUserTestId()
    ) {
      return impressionCache;
    }
  } catch (error) {
    logErrorMsg("fireImpression", error);
  }
  return newImpressionCache;
}

function experimentAlreadyFired(experimentKey: string, variationKey: string) {
  const impressionCache = getImpressionCache();
  return impressionCache.experiments[experimentKey] === variationKey;
}

function setExperimentFired(experimentKey: string, variationKey: string) {
  let impressionCache = getImpressionCache();
  impressionCache.experiments[experimentKey] = variationKey;
  localStorage.setItem(LS_IMPRESSION_KEY, JSON.stringify(impressionCache));
}

function trackInternal(eventName: string, properties: any) {
  // @ts-ignore
  if (window?.tracking?.track) {
    sendTrackEvent();
  } else {
    window.addEventListener("trackingReady", function () {
      sendTrackEvent();
    });
  }

  function sendTrackEvent() {
    // https://app.segment.com/vistaprint/protocols/tracking-plans/rs_1Uvr60Jg3ROZK4boO6jKvuUj2du
    // @ts-ignore
    window.tracking.track(eventName, properties);
  }
}

//It would be better if this is not an IIFE, instead use the script's onLoad attribute but that will require updating the npm package
(function onLoad() {
  if (!isWindowUndefined()) {
    setTimeout(() => {
      window.abReaderInitialized = true;
      if (!isAvailable()) {
        requestCookie();
      }
    }, 200);
  }
})();
